/**
 * SOLID Review
 * S — Strong. One concern: what the SERVER tells a table — its pushed events and the active-round
 *     answer — as the table decisions accept them.
 * O — Good. A new server event is a new schema here and a new op on the contract.
 * L — N/A — no inheritance hierarchy.
 * I — Good. One schema per event; the active round and the reveal are shared by the three that carry them.
 * D — Good. zod and the shared primitives only.
 */
import { z } from 'zod';
import { Identifier, MeyerRank, DieFace } from './primitives';

/**
 * THE SERVER'S SIDE OF THE TABLE (frontend strict-ports plan, FE1, 2026-10-03). Each schema is the
 * GraphQL type the client receives (`@ml3d/contract`, `contract/src/graphql/schema/{round,room,player}.ts`),
 * restricted to the fields a decision reads, with the wire's nullability kept. A field that is non-null
 * on the wire but was guarded in the handler (`!!claim.wasBlind`, `round?.rollerId`) is `nullish` here:
 * the guard was kept for older servers, and so is the leniency.
 *
 * Infrastructure parses the wire into these before it delivers an event (FE1c). A decision may still
 * receive one from an older server, which is why `endsGame` and `loserLivesLeft` are optional.
 */

/** A lift's outcome (`RevealResult` / `ChallengeResult`). */
const PayloadReveal = z.object({
  losingPlayerId: z.string(),
  wasBluff: z.boolean(),
  claimedScore: MeyerRank,
  trueScore: MeyerRank,
  die1: DieFace,
  die2: DieFace,
  newRollerId: z.string(),
  /** The server's word that this lift ended the game (backend §28.191); absent from an older server. */
  endsGame: z.boolean().nullish(),
  /** The loser's lives after the lift, as the table holds them; absent from an older server. */
  loserLivesLeft: z.number().int().nullish(),
}).meta({ id: 'PayloadReveal' });

/** A table's live round as the server holds it (`ActiveRoundInfo`) — pushed in a view, or read on a resync. */
export const PayloadActiveRound = z.object({
  roundId: z.string().nullable(),
  rollerId: z.string().nullable(),
  diceRolled: z.boolean(),
  pendingClaim: z.object({
    claimedScore: MeyerRank,
    label: z.string(),
    challengerId: z.string(),
    wasBlind: z.boolean(),
  }).nullable(),
  minimumScore: MeyerRank.nullable(),
  playerLives: z.array(z.object({ playerId: z.string(), lives: z.number().int() })),
  revealResult: PayloadReveal.nullable(),
}).meta({ id: 'PayloadActiveRound' });

/** `roundStarted` — a game's first round, or the next after a believe, a lift or Play Again. */
export const PayloadRoundStarted = z.object({
  roundId: z.string().nullish(),
  roomId: z.string(),
  rollerId: z.string().nullish(),
  minimumScore: MeyerRank.nullish(),
}).meta({ id: 'PayloadRoundStarted' });

/** `claimAnnounced` — the roller's claim, to every other seat. */
export const PayloadClaimAnnounced = z.object({
  roundId: z.string(),
  claimedScore: MeyerRank,
  label: z.string(),
  wasBlind: z.boolean().nullish(),
  /** The decider the server derived. A legacy round could carry '' — read as none. */
  challengerId: z.string().nullish(),
}).meta({ id: 'PayloadClaimAnnounced' });

/** `cupLifted` — the reveal, to every seat. */
export const PayloadCupLifted = PayloadReveal.extend({ roundId: z.string() }).meta({ id: 'PayloadCupLifted' });

/** `diceRolled` — the roller rolled; no dice values. */
export const PayloadDiceRolled = z.object({ roundId: z.string() }).meta({ id: 'PayloadDiceRolled' });

/** `roomUpdated`, or a `room` read — the roster by id and name. */
export const PayloadRoomRoster = z.object({
  id: z.string(),
  players: z.array(z.object({ id: z.string(), displayName: z.string() })).nullish(),
}).meta({ id: 'PayloadRoomRoster' });

/** `playerSuspended` / `playerResumed`. */
export const PayloadPresence = z.object({
  playerId: z.string().nullish(),
  roomId: z.string().nullish(),
}).meta({ id: 'PayloadPresence' });

/** `roomAbandoned`. */
export const PayloadRoomAbandoned = z.object({ roomId: z.string().nullish() }).meta({ id: 'PayloadRoomAbandoned' });

/** `decisionCountdown` — the server's auto-believe clock for the pending claim. */
export const PayloadDecisionCountdown = z.object({
  roundId: z.string(),
  challengerId: z.string(),
  secondsRemaining: z.number().int(),
}).meta({ id: 'PayloadDecisionCountdown' });

/** `tableView` — the whole table after a save, at the version that save wrote (backend §28.192). */
export const PayloadTableView = z.object({
  roomId: Identifier,
  version: z.number().int(),
  status: z.string(),
  activeRound: PayloadActiveRound.nullable(),
  suspendedPlayerIds: z.array(z.string()),
}).meta({ id: 'PayloadTableView' });

/** The websocket's state, as the transport reports it — any string; the decision maps the unknown ones. */
export const PayloadConnectivity = z.object({ state: z.string() }).meta({ id: 'PayloadConnectivity' });

/** What an event decision answers: whether the event was for this table, or dropped by a guard. */
export const DtoTableAck = z.object({ applied: z.boolean() }).meta({ id: 'DtoTableAck' });

/** What a pushed view did: nothing (older than one applied), only facts, or a missed move too. */
export const DtoTableView = z.object({ outcome: z.enum(['ignored', 'stale', 'in-sync', 'reconciled']) }).meta({ id: 'DtoTableView' });

/** Whether this state change ended a gap, so the Application must catch the table up. */
export const DtoConnectivity = z.object({ resync: z.boolean() }).meta({ id: 'DtoConnectivity' });

export type IPayloadReveal = z.infer<typeof PayloadReveal>;
export type IPayloadActiveRound = z.infer<typeof PayloadActiveRound>;
export type IPayloadRoundStarted = z.infer<typeof PayloadRoundStarted>;
export type IPayloadClaimAnnounced = z.infer<typeof PayloadClaimAnnounced>;
export type IPayloadCupLifted = z.infer<typeof PayloadCupLifted>;
export type IPayloadDiceRolled = z.infer<typeof PayloadDiceRolled>;
export type IPayloadRoomRoster = z.infer<typeof PayloadRoomRoster>;
export type IPayloadPresence = z.infer<typeof PayloadPresence>;
export type IPayloadRoomAbandoned = z.infer<typeof PayloadRoomAbandoned>;
export type IPayloadDecisionCountdown = z.infer<typeof PayloadDecisionCountdown>;
export type IPayloadTableView = z.infer<typeof PayloadTableView>;
export type IPayloadConnectivity = z.infer<typeof PayloadConnectivity>;
export type IEntityTableAck = z.infer<typeof DtoTableAck>;
export type IEntityTableViewOutcome = z.infer<typeof DtoTableView>;
export type IEntityConnectivity = z.infer<typeof DtoConnectivity>;
